import { useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { SOURCE_FIDELITY_EXTENSION_OPTIONS, createMarkdownEditorExtensions } from './editorExtensions'
import { joinFrontMatter, splitFrontMatter } from './frontMatter'
import { getMarkdown } from './markdownStorage'
import { ScrollableContent } from '@/components/ScrollableContent'
import { MarkdownEditorToolbar } from './MarkdownEditorToolbar'
import { cn } from '@/lib/utils'

const editorContentStyles = [
  // Editor root
  '[&_.tiptap]:p-3 [&_.tiptap]:min-h-[200px] [&_.tiptap]:outline-none',
  // Headings
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-foreground [&_h1]:mt-6 [&_h1]:mb-4 [&_h1]:pb-2 [&_h1]:border-b [&_h1]:border-border [&_h1:first-child]:mt-0',
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:pb-1 [&_h2]:border-b [&_h2]:border-border/50',
  '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_h3]:mt-5 [&_h3]:mb-2',
  // Paragraphs
  '[&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4',
  // Lists
  '[&_ul]:list-disc [&_ul]:list-outside [&_ul]:ml-6 [&_ul]:mb-4',
  '[&_ol]:list-decimal [&_ol]:list-outside [&_ol]:ml-6 [&_ol]:mb-4',
  '[&_li]:text-muted-foreground [&_li]:leading-relaxed [&_li]:mb-1',
  // Links
  '[&_a]:text-primary [&_a:hover]:underline',
  // Blockquotes
  '[&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_blockquote]:text-muted-foreground [&_blockquote]:italic',
  // Code
  '[&_code]:px-1.5 [&_code]:py-0.5 [&_code]:bg-muted [&_code]:text-foreground [&_code]:text-sm [&_code]:rounded [&_code]:font-mono',
  '[&_pre]:bg-muted [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:my-4 [&_pre]:overflow-x-auto',
  '[&_pre_code]:p-0 [&_pre_code]:bg-transparent [&_pre_code]:text-sm',
  // Images
  '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-4',
  // Horizontal rule
  '[&_hr]:border-t [&_hr]:border-border [&_hr]:my-6',
  // Tables
  '[&_table]:w-full [&_table]:my-4 [&_table]:border [&_table]:border-border [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted/50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground',
  '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:text-muted-foreground',
  // Inline formatting
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_em]:italic [&_em]:text-muted-foreground',
].join(' ')

// In fidelity mode front matter is held aside and never enters the document, so it cannot be
// flattened. Otherwise the whole value is the body, as this editor has always treated it.
function splitForMode(markdown: string, preserveSourceFidelity: boolean) {
  return preserveSourceFidelity ? splitFrontMatter(markdown) : { frontMatter: null, body: markdown }
}

interface MarkdownEditorProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  dataTestId?: string
  maxHeight?: number | string
  fillHeight?: boolean
  /**
   * For external files that must survive a save unchanged: enables tables and keeps YAML front
   * matter out of the document. Off by default, so app-authored content is unaffected.
   */
  preserveSourceFidelity?: boolean
  /**
   * Registers the table nodes. Imported Azure DevOps markup arrives as GFM pipe tables, and without
   * them the schema keeps only the cell text. Implied by `preserveSourceFidelity`.
   */
  tables?: boolean
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = 'Start writing...',
  className,
  disabled = false,
  id,
  dataTestId,
  maxHeight,
  fillHeight,
  preserveSourceFidelity = false,
  tables = false,
}: MarkdownEditorProps) {
  const isInternalChange = useRef(false)
  const frontMatterRef = useRef(splitForMode(value ?? '', preserveSourceFidelity).frontMatter)

  const editor = useEditor({
    extensions: createMarkdownEditorExtensions(
      preserveSourceFidelity ? SOURCE_FIDELITY_EXTENSION_OPTIONS : { tables },
    ),
    content: splitForMode(value ?? '', preserveSourceFidelity).body,
    editable: !disabled,
    editorProps: {
      attributes: {
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor }) => {
      isInternalChange.current = true
      const body = getMarkdown(editor)
      onChange?.(joinFrontMatter(frontMatterRef.current, body))
    },
  })

  useEffect(() => {
    if (!editor || value === undefined) return

    if (isInternalChange.current) {
      isInternalChange.current = false
      return
    }

    const { frontMatter, body } = splitForMode(value, preserveSourceFidelity)
    frontMatterRef.current = frontMatter

    const currentBody = getMarkdown(editor)
    if (body !== currentBody) {
      // Re-spelling alone lands here — the serializer writes a nested list with two spaces whatever
      // the source used — and emitting would report that as the user's edit, saving a rewrite they
      // never made. tiptap's default is to emit.
      editor.commands.setContent(body, { emitUpdate: false })
    }
  }, [editor, value, preserveSourceFidelity])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [editor, disabled])

  const editorContent = (
    <div className={editorContentStyles}>
      <EditorContent editor={editor} />
    </div>
  )

  return (
    <div
      id={id}
      data-testid={dataTestId}
      className={cn(
        'rounded-md border border-border bg-background',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background',
        disabled && 'cursor-not-allowed opacity-50',
        fillHeight && 'flex-1 min-h-0 flex flex-col',
        className
      )}
    >
      <style>{`
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: var(--muted-foreground);
          opacity: 0.5;
          pointer-events: none;
          height: 0;
        }
      `}</style>
      <MarkdownEditorToolbar editor={editor} />
      {maxHeight || fillHeight ? (
        <ScrollableContent
          maxHeight={fillHeight ? 'none' : maxHeight!}
          className={cn('border-0 rounded-none', fillHeight && 'flex-1 min-h-0')}
          contentClassName="p-0"
        >
          {editorContent}
        </ScrollableContent>
      ) : (
        editorContent
      )}
    </div>
  )
}
